import type { GetV2WorkspacesWorkspaceidNetworkConfigsQueryParamsSortEnumKey } from '@emma/api_client/types';
import { useCallback, useMemo } from 'react';
import { useDebounceValue } from 'usehooks-ts';
import { networkConfigTableColumns } from '@/components/network_config/column_definitions/NetworkConfig';
import { NetworkConfigContent } from '@/components/network_config/NetworkConfigContent';
import { UploadNetworkConfig } from '@/components/network_config/upload/UploadNetworkConfig';
import {
  buildFilterParams,
  type FilterConfig,
  serverTableParams,
} from '@/hooks/common/serverTableParams';
import { useFileDeleteHandler } from '@/hooks/file';
import {
  type NetworkConfigResponse,
  useAddWorkspaceNetworkConfig,
  useCanWriteById,
  useDeleteWorkspaceNetworkConfig,
  useWorkspaceNetworkConfigs,
} from '@/hooks/workspace';
import { TableStoreProvider, useTableStore } from '../common/table/store/Context';
import { Table } from '../common/table/Table';

type Props = {
  workspaceId: number;
  actionOnErr?: () => void;
};

const FILTER_DEBOUNCE_MS = 300;

const FILTERS: FilterConfig = {
  id: 'idIn',
  label: 'labelLike',
  createdAt: ['createdAfter', 'createdBefore'],
  updatedAt: ['updatedAfter', 'updatedBefore'],
};

const NetworkConfigTableContent = ({ workspaceId, actionOnErr }: Props) => {
  const canWrite = useCanWriteById(workspaceId);
  const { pagination, sorting, filters } = useTableStore((s) => s);
  const [debouncedFilters] = useDebounceValue(filters, FILTER_DEBOUNCE_MS);

  const queryParams = {
    ...serverTableParams<GetV2WorkspacesWorkspaceidNetworkConfigsQueryParamsSortEnumKey>(
      pagination,
      sorting
    ),
    ...buildFilterParams(debouncedFilters, FILTERS),
  };

  const { data, isLoading, mutate, isValidating } = useWorkspaceNetworkConfigs(
    workspaceId,
    queryParams
  );

  const totalItems = data?.itemCount || 0;
  const pageCount = Math.ceil(totalItems / pagination.pageSize);

  const addConfigMutation = useAddWorkspaceNetworkConfig(workspaceId);
  const addConfig = useCallback(
    async (uploadData: { fileName: string; fileContents: string; label: string }) => {
      const result = await addConfigMutation(uploadData);
      await mutate();
      return result;
    },
    [addConfigMutation, mutate]
  );

  const deleteConfig = useDeleteWorkspaceNetworkConfig(workspaceId, actionOnErr);
  const { deletingIds, handleDelete } = useFileDeleteHandler(deleteConfig);

  const columns = useMemo(
    () => networkConfigTableColumns(canWrite ? handleDelete : undefined),
    [canWrite, handleDelete]
  );

  const filteredConfigs = useMemo(() => {
    const configs = data?.networkConfigs || [];
    return configs.filter((c) => !deletingIds.has(c.id));
  }, [data?.networkConfigs, deletingIds]) as NetworkConfigResponse[];

  return (
    <>
      <Table<NetworkConfigResponse>
        columns={columns}
        data={filteredConfigs}
        expandableRow={({ original }) => (
          <NetworkConfigContent networkConfigId={original.id} workspaceId={workspaceId} />
        )}
        isLoading={isLoading}
        isValidating={isValidating}
        onRowClick={(_, row) => row.toggleExpanded()}
        refresh={mutate}
        serverSide={{ pageCount, rowCount: totalItems }}
        title="Network Configs"
      />
      {canWrite && <UploadNetworkConfig onUpload={addConfig} />}
    </>
  );
};

export const NetworkConfigTable = (props: Props) => (
  <TableStoreProvider
    initialSorting={[{ desc: true, id: 'id' }]}
    key={`NetworkConfigs-${props.workspaceId}`}
    name={`NetworkConfigs-${props.workspaceId}`}
  >
    <NetworkConfigTableContent {...props} />
  </TableStoreProvider>
);
